import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import "./AdminDashboard.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Endpoints (coerenti con adminUi.js)
const GET_HERO = `${API_BASE}/api/ui/home-hero`; // pubblico (solo lettura)
const PUT_HERO = `${API_BASE}/api/admin/ui/home-hero`; // admin
const DEL_HERO = `${API_BASE}/api/admin/ui/home-hero`; // admin

export default function AdminDashboard() {
  const token = localStorage.getItem("token");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState(false);

  const [currentUrl, setCurrentUrl] = useState(null);
  const [currentAlt, setCurrentAlt] = useState("");
  const [updatedAt, setUpdatedAt] = useState(null);

  const [file, setFile] = useState(null);
  const [alt, setAlt] = useState("");
  const [preview, setPreview] = useState(null);

  // carica stato attuale
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const r = await fetch(GET_HERO, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        const js = await r.json();
        if (!alive) return;
        setCurrentUrl(js?.image_url || null);
        setCurrentAlt(js?.alt || "");
        setUpdatedAt(js?.updated_at || null);
        // precompila input alt con l'attuale
        setAlt(js?.alt || "");
      } catch (e) {
        console.error(e);
        toast.error("Impossibile caricare lo stato attuale");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // gestione anteprima file
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const canSave = useMemo(() => {
    // puoi salvare se:
    // - hai scelto un nuovo file
    // - oppure hai modificato l'ALT
    const altChanged = (alt || "") !== (currentAlt || "");
    return !!file || altChanged;
  }, [file, alt, currentAlt]);

  async function handleSave() {
    if (saving) return;
    if (!canSave) {
      toast("Nessuna modifica da salvare");
      return;
    }
    try {
      setSaving(true);
      const fd = new FormData();
      if (file) fd.append("image", file);
      fd.append("alt", alt || "");
      const r = await fetch(PUT_HERO, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const js = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(js?.error || "Salvataggio fallito");

      setCurrentUrl(js?.image_url || null);
      setCurrentAlt(js?.alt || "");
      setUpdatedAt(js?.updated_at || null);
      // pulizia selezione
      setFile(null);
      setPreview(null);
      toast.success("Immagine aggiornata");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (removing) return;
    if (!currentUrl) {
      toast("Non c’è nessuna immagine da eliminare");
      return;
    }
    if (!window.confirm("Eliminare l’immagine corrente?")) return;
    try {
      setRemoving(true);
      const r = await fetch(DEL_HERO, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const js = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(js?.error || "Eliminazione fallita");

      setCurrentUrl(null);
      setCurrentAlt("");
      setUpdatedAt(js?.updated_at || null);
      // pulizia form
      setFile(null);
      setPreview(null);
      setAlt("");
      toast.success("Immagine eliminata");
    } catch (e) {
      console.error(e);
      toast.error(e.message || "Errore nell’eliminazione");
    } finally {
      setRemoving(false);
    }
  }

  return (
    <div className="ad-wrap">
      <div className="ad-page">
        <h1 className="ad-title">Configurazione UI</h1>

        <section className="ad-card">
          <header className="ad-card-head">
            <h2 className="ad-card-title">Immagine home (colonna destra)</h2>
            {updatedAt && (
              <div className="ad-muted" title={new Date(updatedAt).toString()}>
                Aggiornata il {new Date(updatedAt).toLocaleString("it-IT")}
              </div>
            )}
          </header>

          <div className="ad-grid">
            {/* Colonna sinistra: form */}
            <div className="ad-left">
              <label className="ad-label">File immagine</label>
              <div className="ad-fileRow">
                <input
                  className="ad-file"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    setFile(f);
                  }}
                />
                {file && (
                  <button
                    type="button"
                    className="btn btnLight"
                    onClick={() => {
                      setFile(null);
                      setPreview(null);
                    }}
                  >
                    Annulla selezione
                  </button>
                )}
              </div>

              <label className="ad-label">Testo alternativo (ALT)</label>
              <input
                type="text"
                className="ad-input"
                placeholder="Es. Operatore che stira una camicia"
                value={alt}
                onChange={(e) => setAlt(e.target.value)}
              />

              <div className="ad-actions">
                <button
                  type="button"
                  className="btn btnPrimary"
                  onClick={handleSave}
                  disabled={!canSave || saving}
                  aria-busy={saving}
                >
                  {saving ? "Salvataggio…" : "Salva"}
                </button>
                <button
                  type="button"
                  className="btn btnDanger"
                  onClick={handleDelete}
                  disabled={!currentUrl || removing}
                  aria-busy={removing}
                  title="Elimina immagine corrente"
                >
                  {removing ? "Eliminazione…" : "Elimina"}
                </button>
              </div>

              <p className="ad-hint">
                Formati supportati: JPG, PNG, WebP, GIF. Dimensione massima
                5&nbsp;MB.
              </p>
            </div>

            {/* Colonna destra: preview */}
            <div className="ad-right">
              <div className="ad-preview">
                {preview ? (
                  <img src={preview} alt="Anteprima nuova immagine" />
                ) : currentUrl ? (
                  <img
                    src={currentUrl}
                    alt={currentAlt || "Immagine attuale"}
                  />
                ) : (
                  <div className="ad-empty">Nessuna immagine impostata</div>
                )}
              </div>
            </div>
          </div>
        </section>

        {loading && <div className="ad-loading">Caricamento…</div>}
      </div>
    </div>
  );
}
